import type { IPDFViewerHandle } from '@/components/Organisms/PDFViewer/PDFViewer';
import { useEffect } from 'react';
export interface IHighlightItem {
  page: number;
  matchedText: string | null;
}
const OVERLAY_ROOT_ATTR = 'pdf-qa-overlay-root';
const HIGHLIGHT_COLOR = 'rgb(243 239 165 / 0.4)';
const LETTER_OR_DIGIT_RE = /[\p{L}\p{N}]/u;
const clearHighlights = () => {
  document.querySelectorAll(`[${OVERLAY_ROOT_ATTR}]`).forEach(el => el.remove());
};
export function useQAPdfHighlights(pdfViewerHandle: IPDFViewerHandle) {
  const renderHighlights = (highlights: IHighlightItem[]) => {
    for (const highlight of highlights) {
      renderHighlight(pdfViewerHandle, highlight);
    }
  };
  const scrollToHighlight = (highlight_0: IHighlightItem) => {
    pdfViewerHandle.scrollToPage(highlight_0.page);

    // Remove existing overlay on this page to prevent stacking highlights
    const existingPage = pdfViewerHandle.getPageContainer(highlight_0.page);
    existingPage?.querySelector(`[${OVERLAY_ROOT_ATTR}]`)?.remove();

    // PDF.js renders the text layer asynchronously after scrolling to a page.
    // We poll with requestAnimationFrame until the text spans appear so we
    // can find and highlight the matched text. 15 frames ≈ 250ms at 60fps,
    // which is plenty. Text layers typically render within a couple of frames.
    let attempts = 0;
    const maxAttempts = 15;
    const tryRender = () => {
      attempts++;
      const pageDiv = pdfViewerHandle.getPageContainer(highlight_0.page);
      if (!pageDiv?.clientWidth || !pageDiv.clientHeight) {
        if (attempts < maxAttempts) requestAnimationFrame(tryRender);
        return;
      }

      // Wait until the text layer has at least one span rendered
      const textLayer = pageDiv.querySelector('.textLayer');
      if (highlight_0.matchedText && !textLayer?.querySelector('span') && attempts < maxAttempts) {
        requestAnimationFrame(tryRender);
        return;
      }
      renderHighlight(pdfViewerHandle, highlight_0);
    };
    requestAnimationFrame(tryRender);
  };

  // Remove all highlights when the component unmounts
  useEffect(() => clearHighlights, []);
  return {
    renderHighlights,
    clearHighlights,
    scrollToHighlight
  };
}

// ─── Core highlighting utilities ───────────────────────────────────────────────────────

/** Highlight a single item's matched text on its PDF page. */
function renderHighlight(pdfViewerHandle: IPDFViewerHandle, item: IHighlightItem): HTMLDivElement[] {
  if (!item.matchedText) return [];
  const pageDiv = pdfViewerHandle.getPageContainer(item.page);
  if (!pageDiv?.clientWidth || !pageDiv.clientHeight) return [];
  const root = getOrCreateOverlayRoot(pageDiv);
  return highlightTextOnPage(pageDiv, root, item.matchedText) ?? [];
}

/**
 * Search the PDF page's invisible text layer for `matchedText` and place
 * highlight divs over the matching region.
 *
 * How it works:
 * 1. PDF.js renders an invisible `.textLayer` over each page with <span>
 *    elements containing the page's text. These spans are split at arbitrary
 *    character boundaries (a single word may be spread across 2–3 spans).
 * 2. We walk every text node, concatenate them into one string (no added
 *    spaces — PDF.js already splits mid-word), and fuzzy-search for the quote.
 * 3. Once found, we create DOM Range objects over the matched character region
 *    and ask the browser for the pixel rectangles of those ranges.
 * 4. We place colored divs at those pixel positions inside the overlay root.
 */
function highlightTextOnPage(pageDiv: HTMLDivElement, root: HTMLDivElement, matchedText: string): HTMLDivElement[] | null {
  const textLayer = pageDiv.querySelector('.textLayer');
  if (!textLayer) return null;

  // Walk every text node and build a single concatenated string
  const textNodes: {
    node: Text;
    start: number;
    end: number;
  }[] = [];
  let fullText = '';
  const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT);
  let tNode: Text | null;
  while (tNode = walker.nextNode() as Text | null) {
    if (!tNode.textContent?.length) continue;
    const start = fullText.length;
    fullText += tNode.textContent;
    textNodes.push({
      node: tNode,
      start,
      end: fullText.length
    });
  }
  const match = fuzzySearch(fullText, matchedText);
  if (!match) return null;
  const [matchStart, matchEnd] = match;

  // Create DOM Ranges that span only the matched characters
  const ranges: Range[] = [];
  for (const {
    node,
    start,
    end
  } of textNodes) {
    if (matchStart >= end || matchEnd <= start) continue;
    const range = document.createRange();
    range.setStart(node, Math.max(0, matchStart - start));
    range.setEnd(node, Math.min(end - start, matchEnd - start));
    ranges.push(range);
  }
  if (ranges.length === 0) return null;
  const pixelRects = rangesToRectangles(ranges, root);
  if (pixelRects.length === 0) return null;
  return pixelRects.map(rect => createHighlightDiv(root, rect));
}

// ─── DOM utilities ───────────────────────────────────────────────────────────

/**
 * Get (or create) a transparent overlay div that sits on top of a PDF page.
 * Highlight rectangles are placed inside this overlay so they don't interfere
 * with the page's own content or text selection.
 */
function getOrCreateOverlayRoot(pageDiv: HTMLDivElement): HTMLDivElement {
  let root = pageDiv.querySelector(`[${OVERLAY_ROOT_ATTR}]`) as HTMLDivElement | null;
  if (!root) {
    root = document.createElement('div');
    root.setAttribute(OVERLAY_ROOT_ATTR, '');
    root.style.position = 'absolute';
    root.style.inset = '0';
    root.style.pointerEvents = 'none';
    root.style.userSelect = 'none';
    root.style.zIndex = '2';
    pageDiv.appendChild(root);
  }
  return root;
}

/** Create a single blue highlight rectangle at the given pixel position. */
function createHighlightDiv(root: HTMLDivElement, rect: {
  left: number;
  top: number;
  width: number;
  height: number;
}): HTMLDivElement {
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.left = `${rect.left}px`;
  el.style.top = `${rect.top}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
  el.style.backgroundColor = HIGHLIGHT_COLOR;
  el.style.borderRadius = '2px';
  root.appendChild(el);
  return el;
}

/**
 * Convert browser Range objects into pixel rectangles relative to the overlay
 * root. Each Range may span multiple lines, producing multiple rectangles.
 */
function rangesToRectangles(ranges: Range[], overlayRoot: HTMLDivElement) {
  const rootRect = overlayRoot.getBoundingClientRect();
  const rects: {
    left: number;
    top: number;
    width: number;
    height: number;
  }[] = [];
  for (const range of ranges) {
    for (const clientRect of range.getClientRects()) {
      if (clientRect.width === 0 || clientRect.height === 0) continue;
      rects.push({
        left: clientRect.left - rootRect.left,
        top: clientRect.top - rootRect.top,
        width: clientRect.width,
        height: clientRect.height
      });
    }
  }
  return rects;
}

// ─── Text utilities ──────────────────────────────────────────────────────────

/**
 * Strip `text` to lowercase letters+digits only, and build an index map that
 * records, for each character in the stripped string, its position in the
 * original string. Both are needed for fuzzy matching.
 *
 * Example: "Hi! World" → stripped "hiworld", map [0,1,4,5,6,7,8]
 */
function stripAndMap(text: string): {
  stripped: string;
  indexMap: number[];
} {
  const indexMap: number[] = [];
  let stripped = '';
  for (let i = 0; i < text.length; i++) {
    if (LETTER_OR_DIGIT_RE.test(text[i])) {
      stripped += text[i].toLowerCase();
      indexMap.push(i);
    }
  }
  return {
    stripped,
    indexMap
  };
}

/**
 * Find `needle` inside `haystack`, ignoring all non-alphanumeric characters
 * and case differences. Returns [startIndex, endIndex) mapped back to the
 * ORIGINAL haystack string, or null if not found.
 *
 * Why: PDF text and AI-quoted text often differ in whitespace, punctuation
 * (em-dashes vs hyphens, smart quotes, etc.). Stripping to alphanumeric
 * makes the match resilient to those differences.
 */
function fuzzySearch(haystack: string, needle: string): [number, number] | null {
  const hay = stripAndMap(haystack);
  const {
    stripped: strippedNeedle
  } = stripAndMap(needle);
  if (!strippedNeedle.length) return null;
  const idx = hay.stripped.indexOf(strippedNeedle);
  if (idx === -1) return null;
  const start = hay.indexMap[idx];
  const end = hay.indexMap[idx + strippedNeedle.length - 1] + 1;
  return [start, end];
}